import { useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { Agent } from "@/types/agent/agent";
import { DEFAULT_GENIE_PURPOSE, getPurposeMeta, type GeniePurpose } from "@/types/genie-purpose";
import { GeniePurposePickerSheet } from "./GeniePurposePickerSheet";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface GeniePurposeBadgeProps {
  genie: Agent;
  /** Hide the inline edit affordance (used for read-only contexts like team genies). */
  readOnly?: boolean;
}

export const GeniePurposeBadge = ({ genie, readOnly = false }: GeniePurposeBadgeProps) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const updateAgent = useUpdateAgent();

  const currentPurpose = (genie.purpose ?? DEFAULT_GENIE_PURPOSE) as GeniePurpose;
  const meta = getPurposeMeta(currentPurpose);
  const isGeneral = currentPurpose === DEFAULT_GENIE_PURPOSE;

  const baseClasses =
    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors";
  const toneClasses = isGeneral
    ? "bg-[#F4F6FA] text-[#64748B] border-[#E5EAF0]"
    : "bg-genie-primary/10 text-genie-primary border-genie-primary/20";
  const hoverClasses = isGeneral
    ? "hover:bg-[#EDF1F7] hover:border-[#D1D9E2]"
    : "hover:bg-genie-primary/15 hover:border-genie-primary/30";

  const handleSelect = async (next: GeniePurpose) => {
    if (next === currentPurpose) {
      setIsPickerOpen(false);
      return;
    }
    try {
      await updateAgent.mutateAsync({ agent: genie, updateData: { purpose: next } });
      const nextMeta = getPurposeMeta(next);
      toast.success(`${genie.name} is now a ${nextMeta.label} Genie`);
      setIsPickerOpen(false);
    } catch (error) {
      console.error("Failed to update Genie purpose:", error);
      toast.error(
        error instanceof Error
          ? `Couldn't update purpose: ${error.message}`
          : "Couldn't update Genie purpose. Please try again.",
      );
    }
  };

  const badgeContent = (
    <>
      <GenieIcon name={meta.genieIcon} size={14} className="w-3.5 h-3.5" />
      {meta.label} Genie
      {!readOnly && <Pencil className="w-3 h-3 opacity-60" />}
    </>
  );

  if (readOnly) {
    return <span className={`${baseClasses} ${toneClasses}`}>{badgeContent}</span>;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsPickerOpen(true)}
        className={`${baseClasses} ${toneClasses} ${hoverClasses} cursor-pointer`}
        aria-label={`Change Genie Purpose. Currently: ${meta.label}`}
      >
        {badgeContent}
      </button>

      <GeniePurposePickerSheet
        genie={genie}
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        currentPurpose={currentPurpose}
        onSelect={handleSelect}
        isSaving={updateAgent.isPending}
      />
    </>
  );
};
